import { createSelector } from "@ngxs/store";

export class BaseCollectionsState {
  ///Selectors
  static loading() {
    return createSelector([this], (state: { loading: boolean }) => {
      return state.loading;
    });
  }

  /// ToDo create shared abstract state for all Jtms collections.......

  /// Helpers
  static sortByActive(items: any[], active: string | undefined, direction: 'asc' | 'desc' | undefined) {
    if (!active || !direction) {
      return [...items];
    }
    if (active === 'dateOut') {
      return this.sortByDateField('dateOut', items, direction, 'end')
    } else {
      return [...items].sort((a, b) => {
        if (direction === 'asc') {
          return a[active]?.localeCompare(b[active]);
        } else if (direction === 'desc') {
          return b[active]?.localeCompare(a[active]);
        }
      })
    }
  }

  static sortByDateField(dateField: string, items: any[], direction: 'asc' | 'desc', empty: 'start' | 'end') {
    return [...items].sort((a, b) => {
      const dateA = Date.parse(a[dateField]);
      const dateB = Date.parse(b[dateField]);
      // console.log({dateA, dateB})
      if (Number.isNaN(dateA)) { return empty === 'start' ? -1 : 1 };
      if (Number.isNaN(dateB)) { return empty === 'start' ? 1 : -1 };
      if (dateA === dateB) { return 0 };
      return direction === 'asc' ? dateA > dateB ? 1 : -1 : dateA < dateB ? 1 : -1;
    })
  }

  static sortByDateObject(dateField: string, subField: string, items: any[], direction: 'asc' | 'desc', empty: 'start' | 'end') {
    return [...items].sort((a, b) => {

      const fieldA = a[dateField][subField];
      const fieldB = b[dateField][subField];


      const dateInputA = fieldA?.constructor === Array ? fieldA[fieldA.length - 1] : fieldA;
      const dateInputB = fieldB?.constructor === Array ? fieldB[fieldB.length - 1] : fieldB;

      const dateA = Date.parse(dateInputA);
      const dateB = Date.parse(dateInputB);

      // console.log({dateA, dateB})
      if (Number.isNaN(dateA)) { return empty === 'start' ? -1 : 1 };
      if (Number.isNaN(dateB)) { return empty === 'start' ? 1 : -1 };
      if (dateA === dateB) { return 0 };
      return direction === 'asc' ? dateA > dateB ? 1 : -1 : dateA < dateB ? 1 : -1;
    })
  }

  static sortByAlphabetical(field: string, items: any[]) {
    return [...items].sort((a, b) => {
      return a[field].toUpperCase() < b[field].toUpperCase() ? -1 : a[field].toUpperCase() > b[field].toUpperCase() ? 1 : 0;
    })
  }

  static sortByStatus(items: any[], statuses: string[]) {
    return [...items].sort((a, b) => {
      const aIndex = statuses.indexOf(a.status);
      const bIndex = statuses.indexOf(b.status);
      return aIndex - bIndex;
    })
  }

  /// Mat table filter functions ///
  static filterPredicate: (data: any, filter: string) => boolean = (data: any, filter: string): boolean => {
    // Transform the data into a lowercase string of all property values.
    const dataStr = Object.keys(data as unknown as Record<string, any>)
      .reduce((currentTerm: string, key: string) => {
        // Use an obscure Unicode character to delimit the words in the concatenated string.
        // This avoids matches where the values of two columns combined will match the user's query
        // (e.g. `Flute` and `Stop` will match `Test`). The character is intended to be something
        // that has a very low chance of being typed in by somebody in a text field. This one in
        // particular is "White up-pointing triangle with dot" from
        // https://en.wikipedia.org/wiki/List_of_Unicode_characters
        return currentTerm + (data as unknown as Record<string, any>)[key] + '◬';
      }, '')
      .toLowerCase();

    // Transform the filter by converting it to lowercase and removing whitespace.
    const transformedFilter = filter.trim().toLowerCase();

    return dataStr.indexOf(transformedFilter) != -1;
  };

  /**
   * Dataconnect value sanitization helpers
   */
  protected _convertToBoolean(value: any): boolean {
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'string') {
      const lower = value.toLowerCase().trim();
      if (lower === 'true' || lower === '1') {
        return true;
      } else if (lower === 'false' || lower === '0' || lower === '') {
        return false;
      }
    }
    if (typeof value === 'number') {
      return value === 1;
    }
    return false;
  }

  protected _sliceArrayIntoChunks<T>(array: T[], chunkSize: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  }

  protected _convertToNumberOrLeaveNull(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;

    // If already a number, return it
    if (typeof value === 'number') return value;

    // Strip all non-numeric characters except decimal point, negative sign, and leading plus
    const cleaned = String(value).replace(/[^\d.-]/g, '');

    if (cleaned === '' || cleaned === '-' || cleaned === '.') return null;

    const number = parseFloat(cleaned);
    return isNaN(number) ? null : number;
  }

  protected _convertToDateOrLeaveNull(value: any) {
    if (!value) return null;

    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      return date;
    }

    return null;
  }

  protected _checkIfTimestampOrDate(value: any): string | null {
    if (!value) return null;

    // If already a timestamp string (ISO format), return as-is
    if (typeof value === 'string' && !isNaN(Date.parse(value))) {
      return new Date(value).toISOString();
    }

    // If it's a Date object, convert to timestamp
    if (value instanceof Date) {
      return value.toISOString();
    }

    // Try to parse as date and convert to timestamp
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      return date.toISOString();
    }

    return null;
  }
}
